import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, BarChart3, TrendingUp, Users, Clock, Heart, Calendar, Users2, Bookmark, Crown, Award, Activity, PieChart, ExternalLink } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts'
import { Button } from '@/components/ui/button'
import { Avatar } from '@/components/ui/avatar'
import { watchlistService } from '@/services'

interface WatchlistAccount {
  id: string
  username: string
  full_name: string
  followers: number
  engagement_rate: number
  avg_view_rate: number
  posts_analyzed: number
  avg_likes: number
  avg_comments: number
  category: string
  is_verified: boolean
  last_analyzed: string
  posts_per_day: number
}

interface WatchlistData {
  id: string
  name: string
  description: string
  created_at: string
  last_updated: string
  total_accounts: number
  accounts: WatchlistAccount[]
  aggregate_metrics: {
    total_followers: number
    avg_engagement_rate: number
    total_posts_analyzed: number
    total_likes: number
    total_comments: number
    content_distribution: { type: string; count: number; percentage: number }[]
    category_distribution: { category: string; count: number; percentage: number }[]
  }
  performance_insights: {
    top_performer: WatchlistAccount
    fastest_growing: WatchlistAccount
    highest_engagement: WatchlistAccount
    most_active: WatchlistAccount
  }
  strategic_insights: {
    strengths: string[]
    opportunities: string[]
    recommendations: string[]
    trends: string[]
  }
}



interface MetricCardProps {
  title: string
  value: string | number
  description?: string
  icon: React.ReactNode
  trend?: string
  color?: string
}

function MetricCard({ title, value, description, icon, trend, color = 'blue' }: MetricCardProps) {
  const colorClasses = {
    blue: 'from-blue-400/20 to-transparent text-blue-500 text-blue-600',
    green: 'from-green-400/20 to-transparent text-green-500 text-green-600',
    pink: 'from-pink-400/20 to-transparent text-pink-500 text-pink-600',
    purple: 'from-purple-400/20 to-transparent text-purple-500 text-purple-600',
    orange: 'from-orange-400/20 to-transparent text-orange-500 text-orange-600'
  }
  
  const [gradientClass, iconClass, textClass] = colorClasses[color as keyof typeof colorClasses].split(' ')

  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl ${gradientClass} rounded-full -translate-y-4 translate-x-4`} />
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={`${iconClass}`}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${iconClass}`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
        {trend && (
          <p className={`text-xs ${textClass} flex items-center gap-1 mt-1`}>
            <TrendingUp className="h-3 w-3" />
            {trend}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function formatFollowers(count: number): string {
  if (count >= 1000000000) return `${(count / 1000000000).toFixed(1)}B`
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
  return count.toString()
}

function formatEngagementRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

function LoadingState() {
  return (
    <div className="space-y-6 min-h-[70vh]">
      {/* Page header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-4 w-72" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="space-y-0 pb-2">
              <Skeleton className="h-4 w-[100px]" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-[80px] mb-2" />
              <Skeleton className="h-3 w-[120px]" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-[150px]" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[200px] w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex justify-center items-center min-h-[400px]">
      <Alert className="max-w-md">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="text-center">
          {message || 'Failed to load watchlist analytics data'}
        </AlertDescription>
      </Alert>
    </div>
  )
}

function AccountPerformanceGrid({ accounts, navigate }: { accounts: WatchlistAccount[], navigate: (path: string) => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users2 className="h-5 w-5" />
            Account Performance Overview
          </CardTitle>
          <CardDescription>
            Individual metrics for each account in the watchlist
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {accounts.map((account) => (
              <Card key={account.id} className="group hover:shadow-md transition-all duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-sm font-semibold">
                        {account.username.slice(0, 2).toUpperCase()}
                      </div>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-sm font-semibold truncate">
                          {account.full_name}
                        </CardTitle>
                        {account.is_verified && (
                          <Badge variant="secondary" className="text-xs px-1 py-0">✓</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        @{account.username}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-muted-foreground">Followers</p>
                      <p className="font-semibold">{(account.followers / 1000000).toFixed(1)}M</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Engagement</p>
                      <p className="font-semibold text-green-600">{formatEngagementRate(account.engagement_rate)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">Performance</span>
                      <span className="text-xs font-medium">{formatEngagementRate(account.engagement_rate)}</span>
                    </div>
                    <Progress 
                      value={account.engagement_rate * 100} 
                      className="h-2"
                      style={{ '--progress-foreground': '#10b981' } as React.CSSProperties}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button asChild size="sm" variant="outline" className="flex-1 text-xs">
                      <a href={`https://instagram.com/${account.username}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View
                      </a>
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 text-xs"
                      onClick={() => navigate(`/instagram/account/${account.id}`)}
                    >
                      <BarChart3 className="h-3 w-3 mr-1" />
                      Analytics
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function TopPerformersSection({ insights }: { insights: WatchlistData['performance_insights'] }) {
  const performers = [
    { title: 'Most Followed', account: insights.top_performer, icon: Crown, color: 'text-yellow-600' },
    { title: 'Highest Engagement', account: insights.highest_engagement, icon: Heart, color: 'text-pink-600' },
    { title: 'Most Active', account: insights.most_active, icon: Activity, color: 'text-blue-600' },
    { title: 'Fastest Growing', account: insights.fastest_growing, icon: TrendingUp, color: 'text-green-600' }
  ]

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Performance Highlights
          </CardTitle>
          <CardDescription>
            Standout accounts across different metrics
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {performers.map((performer) => (
              <div key={performer.title} className="p-4 rounded-lg border bg-muted/20">
                <div className="flex items-center gap-2 mb-3">
                  <performer.icon className={`h-4 w-4 ${performer.color}`} />
                  <span className="text-sm font-medium">{performer.title}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <div className="w-full h-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white text-xs font-semibold">
                        {performer.account.username.slice(0, 2).toUpperCase()}
                      </div>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{performer.account.full_name}</p>
                      <p className="text-xs text-muted-foreground truncate">@{performer.account.username}</p>
                    </div>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Followers:</span>
                      <span className="font-medium">{(performer.account.followers / 1000000).toFixed(1)}M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {performer.title === 'Fastest Growing' ? 'Virality:' : 
                         performer.title === 'Most Active' ? 'Posts/day:' : 'Engagement:'}
                      </span>
                      <span className="font-medium text-green-600">
                        {performer.title === 'Fastest Growing' 
                          ? formatEngagementRate(performer.account.avg_view_rate || performer.account.engagement_rate)
                          : performer.title === 'Most Active'
                          ? performer.account.posts_per_day
                          : formatEngagementRate(performer.account.engagement_rate)
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function TopPostsSection({ topPosts }: { topPosts?: any[] }) {
  if (!topPosts || topPosts.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Top Performing Posts
            </CardTitle>
            <CardDescription>
              Best posts across all accounts in this watchlist
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <TrendingUp className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No standout posts found across watchlist accounts</p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top Performing Posts
          </CardTitle>
          <CardDescription>
            Best posts across all accounts in this watchlist (up to 5)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 max-h-[550px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
            {topPosts.map((post, index) => {
              const caption = post.caption || "No caption available"
              const truncatedCaption = caption.length > 100 ? `${caption.substring(0, 100)}...` : caption
              
              return (
                <a 
                  key={post.post_id}
                  href={`https://instagram.com/p/${post.shortcode}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-lg border hover:shadow-md transition-all duration-200 cursor-pointer group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        @{post.username}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(post.timestamp * 1000).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                        View Post
                      </span>
                    </div>
                  </div>
                  
                  <p className="mb-3 text-sm text-muted-foreground line-clamp-2">
                    {truncatedCaption}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {post.full_name}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div 
                          className="h-2 bg-green-500 rounded-full"
                          style={{ width: `${Math.min(post.engagement_rate * 100, 100)}%` }}
                        />
                      </div>
                      <span className="font-bold text-green-700 text-sm">
                        {(post.engagement_rate * 100).toFixed(3)}%
                      </span>
                    </div>
                  </div>
                </a>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

function WatchlistDashboard({ data, navigate }: { data: WatchlistData, navigate: (path: string) => void }) {
  const { aggregate_metrics, performance_insights, accounts } = data

  // Prepare chart data for account comparison
  const comparisonChartData = accounts
    .sort((a, b) => b.engagement_rate - a.engagement_rate)
    .slice(0, 6)
    .map(account => ({
      name: `@${account.username}`,
      engagement: (account.engagement_rate * 100),
      followers: account.followers / 1000000
    }))

  const chartConfig = {
    engagement: { label: "Engagement Rate (%)", color: "hsl(var(--chart-1))" },
    followers: { label: "Followers (M)", color: "hsl(var(--chart-2))" }
  } satisfies ChartConfig

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Watchlist Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Bookmark className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-2xl">{data.name}</CardTitle>
              </div>
              {data.description && (
                <CardDescription className="text-base mt-2 max-w-2xl">
                  {data.description}
                </CardDescription>
              )}
              <div className="flex items-center gap-4 mt-3">
                <Badge variant="outline" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  {data.total_accounts} accounts
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  Created {formatDate(data.created_at)}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  <Clock className="h-3 w-3 mr-1" />
                  Updated {formatDate(data.last_updated)}
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Aggregate Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Followers"
          value={formatFollowers(aggregate_metrics.total_followers)}
          description="Across all accounts"
          icon={<Users className="h-5 w-5" />}
          color="blue"
        />
        <MetricCard
          title="Avg Engagement Rate"
          value={formatEngagementRate(aggregate_metrics.avg_engagement_rate)}
          description="Collective performance"
          icon={<Heart className="h-5 w-5" />}
          color="pink"
        />
        <MetricCard
          title="Posts Analyzed"
          value={aggregate_metrics.total_posts_analyzed}
          description="Total content reviewed"
          icon={<BarChart3 className="h-5 w-5" />}
          color="green"
        />
        <MetricCard
          title="Total Engagement"
          value={`${((aggregate_metrics.total_likes + aggregate_metrics.total_comments) / 1000000).toFixed(1)}M`}
          description="Likes + Comments"
          icon={<Activity className="h-5 w-5" />}
          color="purple"
        />
      </div>

      {/* Performance Highlights */}
      <TopPerformersSection insights={performance_insights} />

      <div className="grid gap-6 md:grid-cols-2">
        {/* Content Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Content Distribution
            </CardTitle>
            <CardDescription>
              Content types across all accounts
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
            {aggregate_metrics.content_distribution.map((item, idx) => (
              <div key={item.type + idx} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${
                      ['bg-blue-500', 'bg-green-500', 'bg-amber-500'][idx] || 'bg-gray-500'
                      }`} />
                      <span className="font-medium">{item.type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}</span>
                    </div>
                    <Badge variant="secondary">{item.count} posts</Badge>
                  </div>
                  <div className="space-y-1">
                    <Progress 
                      value={item.percentage} 
                      className="h-3"
                      style={{
                        '--progress-foreground': ['#3b82f6', '#10b981', '#f59e0b'][idx] || '#6b7280'
                      } as React.CSSProperties}
                    />
                    <div className="text-right text-sm font-semibold text-muted-foreground">
                      {item.percentage}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Account Comparison Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Account Performance Comparison
            </CardTitle>
            <CardDescription>
              Top accounts by engagement rate
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="name" 
                    className="text-xs fill-muted-foreground"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis className="text-xs fill-muted-foreground" />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="engagement" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Account Performance Grid */}
      <AccountPerformanceGrid accounts={accounts} navigate={navigate} />

      {/* Top Posts */}
      <TopPostsSection topPosts={(performance_insights as any).top_posts} />

      {/* Processing Info */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Watchlist analytics updated on {formatDate(data.last_updated)}</span>
            <Badge variant="outline">Analytics Ready</Badge>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export function InstagramWatchlistPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  
  // Real API query
  const { data, isLoading, error } = useQuery({
    queryKey: ['watchlist-analytics', uuid],
    queryFn: async () => {
      return await watchlistService.getWatchlist(uuid!)
    },
    enabled: !!uuid && isAuthenticated && !authLoading,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  if (isLoading) {
    return <LoadingState />
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'An error occurred'
    if (errorMessage.includes('not found') || errorMessage.includes('404')) {
      return <ErrorState message="Watchlist not found" />
    }
    return <ErrorState message={errorMessage} />
  }

  if (!data) {
    return <ErrorState message="No watchlist data available" />
  }

  return <WatchlistDashboard data={data} navigate={navigate} />
} 